import React, { createContext, useContext, useState } from 'react';
import config from '../config';
import apiClient from '../services/api-client';
import { useClerkAuth } from './use-clerk-auth';

const SelectedFilesContext = createContext();

export const SelectedFilesProvider = ({ children }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [fileDataMap, setFileDataMap] = useState({}); // to store file_data temporarily
    const { getSessionToken } = useClerkAuth();

    const handleFileSelect = (files) => {
        setSelectedFiles(files);
    };

    const clearSelectedFiles = () => {
        setSelectedFiles([]);
    };

    const clearFileDataMap = () => {
        setFileDataMap({});
    };

    const handleUpload = async ({
        onSuccess = () => {console.log('success')},
        onError = () => {console.log('error')}
    }) => {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        try {
            const formData = new FormData();
            for (const file of selectedFiles) {
                formData.append("files", file);
                formData.append("timezone", userTimezone);
            }

            console.log('Uploading files:', selectedFiles);
            const response = await apiClient.post('/upload', formData, getSessionToken);
            console.log('Full response:', response);

            // Handle the new standardized response format
            if (response.status === "success" && response.data && response.data.courses) {
                const responseData = response.data; // Extract the actual data
                const newFileDataMap = {}; // store file_data separately for later use
                // Transform courses array into the expected format for compatibility
                const sectionsFormat = {};
                const coursesForStorage = responseData.courses.map(course => {
                    // Save file_data separately (in memory for this session)
                    if (course.file_data) {
                        newFileDataMap[course.pdf] = course.file_data; // local temp map
                    }
                    // Create course object WITHOUT file_data for localStorage
                    const courseWithoutFileData = {
                        course_name: course.course_name,
                        pdf: course.pdf,
                        sections: course.sections,
                        timezone: course.timezone,
                        institution: course.institution,
                        location: course.location
                    };
                    sectionsFormat[course.pdf] = courseWithoutFileData;
                    return courseWithoutFileData;
                });

                setFileDataMap(prev => ({ ...prev, ...newFileDataMap })); // update state with file_data map
                console.log('Transformed sections format:', sectionsFormat);
                localStorage.setItem("courses", JSON.stringify(coursesForStorage));
                localStorage.setItem("sections", JSON.stringify(sectionsFormat));
                localStorage.setItem("fallback_required", JSON.stringify(responseData.fallback_required));
                localStorage.setItem("needs_timezone_selection", JSON.stringify(responseData.needs_timezone_selection));
                return onSuccess(sectionsFormat);
            } else {
                throw new Error(response.message || "Error processing files!");
            }
        } catch (error) {
            console.error('Upload error:', error);
            
            // The apiClient throws Error with message "API request failed: {json}"
            if (error.message && error.message.startsWith('API request failed: ')) {
                try {
                    // Extract the JSON part after "API request failed: "
                    const jsonStr = error.message.substring('API request failed: '.length);
                    const errorData = JSON.parse(jsonStr);
                    
                    // Check if this is a file size error
                    if (errorData.error_type === "file_size_exceeded") {
                        console.log('File size error detected, passing full error object to onError');
                        return onError(errorData);
                    } else {
                        // For other API errors, pass the error message
                        return onError(errorData.message || error.message);
                    }
                } catch (parseError) {
                    console.error('Failed to parse error response:', parseError);
                    // If parsing fails, just pass the original error message
                    return onError(error.message);
                }
            } else if (error.message) {
                // For other errors (like auth errors), pass the message
                return onError(error.message);
            } else {
                // Fallback
                return onError("Error processing files!");
            }
        } finally {
            setSelectedFiles([]);
        }
    };


    return (
        <SelectedFilesContext.Provider 
            value={{ 
                selectedFiles, 
                handleFileSelect, 
                clearSelectedFiles,
                handleUpload,
                fileDataMap,
                clearFileDataMap
            }}
        >
            {children}
        </SelectedFilesContext.Provider>
    );
};

export const useSelectedFiles = () => {
    const context = useContext(SelectedFilesContext);
    if (!context) {
        throw new Error('useSelectedFiles must be used within a SelectedFilesProvider');
    }
    return context;
};
